import { useEffect, useState } from "react";
import ActionButton from "@/components/ActionButton";
import api from "@/lib/api";
import { Plus, Search, Mail, Phone, ExternalLink } from "lucide-react";
import ContactDetailModal from "@/components/ContactDetailModal";

export default function Clients() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [show, setShow] = useState(false);
  const [detail, setDetail] = useState(null);
  const [filter, setFilter] = useState("all"); // all | client | partenaire

  const load = async () => {
    // Charge clients ET partenaires : une fiche peut porter les deux rôles
    const { data } = await api.get("/contacts", { params: { type: "client,partenaire", ...(q ? { q } : {}) } });
    setList(data);
  };
  useEffect(() => { load(); }, [q]);

  const create = async (e) => {
    e.preventDefault();
    const fd = Object.fromEntries(new FormData(e.target).entries());
    const type = fd._type || "client";
    delete fd._type;
    await api.post("/contacts", { ...fd, types: [type] });
    setShow(false);
    load();
  };

  const visible = list.filter((c) => {
    if (filter === "all") return true;
    return (c.types || []).includes(filter);
  });

  const counts = {
    all: list.length,
    client: list.filter((c) => (c.types || []).includes("client")).length,
    partenaire: list.filter((c) => (c.types || []).includes("partenaire")).length,
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">Carnet</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">Clients &amp; Partenaires</h1>
          <p className="text-sm text-stone-500 mt-1">{visible.length} fiche{visible.length > 1 ? "s" : ""} · une fiche peut porter plusieurs rôles.</p>
        </div>
        <button data-testid="client-new" onClick={() => setShow(true)} className="bg-stone-900 hover:bg-black text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2"><Plus size={16}/> Ajouter</button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-xl flex-1">
          <Search size={16} className="absolute left-3 top-2.5 text-stone-400"/>
          <input data-testid="client-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Recherche : nom, société, email, ville…" className="w-full pl-9 pr-3 py-2 border border-stone-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-stone-900"/>
        </div>
        <div className="flex gap-1 bg-stone-100 rounded-lg p-1" data-testid="client-filter">
          {[
            { v: "all", l: "Tous" },
            { v: "client", l: "Clients" },
            { v: "partenaire", l: "Partenaires" },
          ].map((t) => (
            <button
              key={t.v}
              data-testid={`client-filter-${t.v}`}
              onClick={() => setFilter(t.v)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition ${filter === t.v ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-700"}`}
            >
              {t.l} <span className="text-[10px] text-stone-400 ml-1">{counts[t.v]}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {visible.map((c) => (
          <div key={c.id} className="bg-white border border-stone-200 hover:border-stone-300 rounded-2xl p-5 transition cursor-pointer" data-testid={`client-${c.id}`} onClick={() => setDetail(c)}>
            <div className="font-display font-semibold text-base truncate">{c.nom}</div>
            {c.societe && c.societe !== c.nom && <div className="text-xs text-stone-500 truncate">{c.societe}</div>}
            <div className="flex flex-wrap gap-1 mt-1.5">
              {(c.types || []).map((t) => (
                <span key={t} className={`pill text-[10px] capitalize ${t === "client" ? "bg-stone-100 text-stone-700" : t === "partenaire" ? "bg-amber-50 text-amber-800" : "bg-stone-50 text-stone-500"}`} data-testid={`client-type-${c.id}-${t}`}>
                  {t}
                </span>
              ))}
              {c.partenaire_type && <span className="pill bg-rose-50 text-rose-800 text-[10px]">{c.partenaire_type}</span>}
              {c.taux_commission > 0 && <span className="pill bg-emerald-50 text-emerald-700 text-[10px] font-mono">{c.taux_commission}%</span>}
              {c.ville && <span className="pill bg-stone-100 text-stone-600 text-[10px]">{c.ville}</span>}
            </div>
            <div className="mt-3 text-sm space-y-1">
              {c.email && (
                <a onClick={(e) => e.stopPropagation()} href={`mailto:${c.email}`} className="inline-flex items-center gap-1 text-xs text-stone-600 hover:text-stone-900 truncate">
                  <Mail size={11}/> {c.email}
                </a>
              )}
              {c.telephone && (
                <a onClick={(e) => e.stopPropagation()} href={`tel:${c.telephone.replace(/\s/g, '')}`} className="inline-flex items-center gap-1 text-xs text-stone-600 hover:text-stone-900">
                  <Phone size={11}/> {c.telephone}
                </a>
              )}
              {c.linkedin && (
                <a onClick={(e) => e.stopPropagation()} href={c.linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-900">
                  <ExternalLink size={11}/> LinkedIn
                </a>
              )}
            </div>
          </div>
        ))}
        {visible.length === 0 && <div className="col-span-full text-center text-stone-400 py-12">Aucune fiche.</div>}
      </div>

      {detail && (
        <ContactDetailModal
          item={detail}
          onClose={() => setDetail(null)}
          onSaved={() => load()}
          onDeleted={() => load()}
        />
      )}

      {show && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form onSubmit={create} className="bg-white rounded-2xl p-6 max-w-lg w-full" data-testid="client-form">
            <h3 className="font-display text-2xl font-bold mb-4">Nouvelle fiche</h3>
            <div className="space-y-3 text-sm">
              <label className="flex flex-col">
                <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold mb-1">Type initial</span>
                <select name="_type" defaultValue="client" data-testid="client-type-select" className="w-full border border-stone-200 rounded-lg px-3 py-2">
                  <option value="client">Client</option>
                  <option value="partenaire">Partenaire</option>
                </select>
              </label>
              <input name="nom" required placeholder="Nom contact ou société" className="w-full border border-stone-200 rounded-lg px-3 py-2"/>
              <input name="societe" placeholder="Société (si différente)" className="w-full border border-stone-200 rounded-lg px-3 py-2"/>
              <input name="email" type="email" placeholder="Email" className="w-full border border-stone-200 rounded-lg px-3 py-2"/>
              <input name="telephone" placeholder="Téléphone" className="w-full border border-stone-200 rounded-lg px-3 py-2"/>
              <input name="ville" placeholder="Ville" className="w-full border border-stone-200 rounded-lg px-3 py-2"/>
            </div>
            <div className="flex gap-2 mt-5 justify-end">
              <button type="button" onClick={() => setShow(false)} className="px-4 py-2 text-sm border border-stone-200 rounded-lg">Annuler</button>
              <ActionButton type="button" onClick={create} data-testid="client-submit" successLabel="Créé" className="px-4 py-2 text-sm bg-stone-900 text-white rounded-lg font-semibold">Créer</ActionButton>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
